
import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Users, Eye, MousePointer, ArrowUpRight, Edit, Save, Image as ImageIcon, CreditCard, BarChart3, Inbox, CheckSquare, ShieldCheck, MessageSquare, Settings, Zap, Search } from 'lucide-react';
import { CheckoutModal } from './shared/CheckoutModal';
import { CommunicationHub } from './shared/CommunicationHub';
import { SettingsView } from './shared/SettingsView';
import { MOCK_SUBSCRIPTION, MOCK_INVOICES, MOCK_CONSULTANCY_ANALYTICS, MOCK_RFQS } from '../constants';
import { MOCK_VERIFICATION_REQUESTS } from '../constants';
import { RFQDetail } from './shared/RFQDetail';
import { RFQ } from '../types';

const data = [
  { name: 'Jan', views: 400, leads: 24 },
  { name: 'Feb', views: 300, leads: 13 },
  { name: 'Mar', views: 200, leads: 38 },
  { name: 'Apr', views: 278, leads: 39 },
  { name: 'May', views: 189, leads: 48 },
  { name: 'Jun', views: 239, leads: 38 },
];

const StatCard: React.FC<{ title: string; value: string; trend: string; icon: any }> = ({ title, value, trend, icon: Icon }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
    <div className="flex items-center justify-between mb-4">
      <div className="p-2 bg-blue-50 rounded-lg">
        <Icon className="w-6 h-6 text-blue-600" />
      </div>
      <span className="text-green-600 text-sm font-medium flex items-center bg-green-50 px-2 py-1 rounded-full">
        <ArrowUpRight className="w-3 h-3 mr-1" /> {trend}
      </span>
    </div>
    <h3 className="text-slate-500 text-sm font-medium">{title}</h3>
    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
  </div>
);

export const ConsultantDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'opportunities' | 'profile' | 'billing' | 'analytics' | 'inbox' | 'verification' | 'settings'>('overview');
  const [selectedOpportunity, setSelectedOpportunity] = useState<RFQ | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [profileData, setProfileData] = useState({
      name: 'BioReg Global Strategies',
      tagline: 'Specialized FDA & EMA Strategy for Gene Therapies',
      description: 'BioReg Global Strategies focuses exclusively on advanced therapy medicinal products (ATMPs).',
      rateStart: 350,
      rateEnd: 550
  });

  const handleSaveProfile = () => {
      alert('Profile changes saved! (Mock action)');
  };

  return (
    <div className="flex h-screen bg-slate-50">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 flex flex-col">
            <div className="p-6 border-b border-slate-200">
                <h1 className="font-bold text-xl text-slate-900">Vendor Portal</h1>
                <p className="text-xs text-slate-500 mt-1">BioReg Global</p>
            </div>
            <nav className="flex-grow p-4 space-y-1 overflow-y-auto">
                <button onClick={() => { setActiveTab('overview'); setSelectedOpportunity(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeTab === 'overview' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <BarChart3 className="w-5 h-5 mr-3" /> Overview
                </button>
                <button onClick={() => { setActiveTab('opportunities'); setSelectedOpportunity(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeTab === 'opportunities' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Zap className="w-5 h-5 mr-3" /> Opportunities
                </button>
                <button onClick={() => { setActiveTab('inbox'); setSelectedOpportunity(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeTab === 'inbox' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Inbox className="w-5 h-5 mr-3" /> Inbox & Tasks
                </button>
                <button onClick={() => { setActiveTab('profile'); setSelectedOpportunity(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeTab === 'profile' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Edit className="w-5 h-5 mr-3" /> Edit Profile
                </button>
                <button onClick={() => { setActiveTab('verification'); setSelectedOpportunity(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeTab === 'verification' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <ShieldCheck className="w-5 h-5 mr-3" /> Verification Center
                </button>
                <button onClick={() => { setActiveTab('analytics'); setSelectedOpportunity(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeTab === 'analytics' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <LineChart className="w-5 h-5 mr-3" /> Performance
                </button>
                <button onClick={() => { setActiveTab('billing'); setSelectedOpportunity(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeTab === 'billing' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <CreditCard className="w-5 h-5 mr-3" /> Subscription
                </button>
                <button onClick={() => { setActiveTab('settings'); setSelectedOpportunity(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeTab === 'settings' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Settings className="w-5 h-5 mr-3" /> Settings
                </button>
            </nav>
        </div>

        {/* Main Content */}
        <div className="flex-grow overflow-y-auto p-8">
            {activeTab === 'overview' && (
                <div className="space-y-8">
                    <h2 className="text-2xl font-bold text-slate-900">Dashboard Overview</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <StatCard title="Total Profile Views" value="2,345" trend="+12%" icon={Eye} />
                        <StatCard title="Qualified Leads" value="142" trend="+8%" icon={Users} />
                        <StatCard title="Click-Through Rate" value="4.6%" trend="+2.1%" icon={MousePointer} />
                    </div>
                    
                    {/* Recent Opportunities Snippet */}
                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-slate-900">New Opportunities</h3>
                            <button onClick={() => setActiveTab('opportunities')} className="text-sm text-blue-600 font-medium hover:underline">View All</button>
                        </div>
                        <div className="space-y-4">
                            {MOCK_RFQS.slice(0,2).map(rfq => (
                                <div key={rfq.id} className="flex items-center justify-between p-4 border border-slate-100 rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => { setActiveTab('opportunities'); setSelectedOpportunity(rfq); }}>
                                    <div>
                                        <h4 className="font-medium text-slate-900">{rfq.title}</h4>
                                        <p className="text-xs text-slate-500">{rfq.regions.join(', ')} • {rfq.therapeuticArea}</p>
                                    </div>
                                    <button className="text-sm bg-white border border-blue-200 text-blue-700 px-3 py-1 rounded font-medium">View</button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                        <h3 className="text-lg font-medium text-slate-900 mb-6">Lead Volume Trend</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b'}} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b'}} />
                                <Tooltip contentStyle={{backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0'}} cursor={{fill: '#f1f5f9'}}/>
                                <Bar dataKey="leads" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'opportunities' && (
                selectedOpportunity ? (
                    <RFQDetail 
                        rfq={selectedOpportunity} 
                        userRole="consultant" 
                        onBack={() => setSelectedOpportunity(null)} 
                    />
                ) : (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                            <Zap className="w-6 h-6 text-yellow-500 fill-current" /> Open Opportunities
                        </h2>
                        
                        {/* Filter/Search bar mock */}
                        <div className="flex gap-4 mb-6">
                            <div className="relative flex-grow">
                                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                                <input type="text" placeholder="Search projects by keywords, therapeutic area..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg" />
                            </div>
                            <select className="border border-slate-300 rounded-lg px-4 py-2 bg-white text-slate-600">
                                <option>All Regions</option>
                            </select>
                        </div>

                        <div className="grid gap-4">
                            {MOCK_RFQS.map(rfq => (
                                <div key={rfq.id} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedOpportunity(rfq)}>
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-lg font-bold text-slate-900">{rfq.title}</h3>
                                        <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase">{rfq.status}</span>
                                    </div>
                                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{rfq.description}</p>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">{rfq.regions.join(', ')}</span>
                                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">{rfq.therapeuticArea}</span>
                                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">{rfq.pathway || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between items-center border-t border-slate-100 pt-4">
                                        <div className="text-xs text-slate-500">
                                            Posted: {rfq.postedDate} • Budget: {rfq.budgetRange}
                                        </div>
                                        <button className="text-sm text-blue-600 font-medium hover:underline">View Details & Apply &rarr;</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}

            {activeTab === 'profile' && (
                <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                    <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-slate-900">Company Profile Settings</h3>
                        <button onClick={handleSaveProfile} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            <Save className="w-4 h-4 mr-2" /> Save Changes
                        </button>
                    </div>
                    <div className="p-6 space-y-6 max-w-3xl">
                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                                <input type="text" value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} className="w-full p-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Tagline</label>
                                <input type="text" value={profileData.tagline} onChange={(e) => setProfileData({...profileData, tagline: e.target.value})} className="w-full p-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                                <textarea rows={4} value={profileData.description} onChange={(e) => setProfileData({...profileData, description: e.target.value})} className="w-full p-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'verification' && (
                <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-slate-900">Verification Center</h2>
                    <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                        <h3 className="font-bold text-blue-900 mb-2">Why verify?</h3>
                        <p className="text-blue-700 text-sm">Verified consultancies receive 3x more leads and the "Regulatory Excellence" badge.</p>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-200 p-6">
                        <h3 className="text-lg font-medium text-slate-900 mb-4">Submit New Claim</h3>
                        <div className="grid grid-cols-1 gap-4 max-w-lg">
                             <select className="p-2 border border-slate-300 rounded">
                                 <option>Select Claim Type...</option>
                                 <option>Drug Approval</option>
                                 <option>Orphan Designation</option>
                             </select>
                             <textarea className="p-2 border border-slate-300 rounded h-24" placeholder="Describe details (Drug name, Date, Agency)..."></textarea>
                             <div className="border-2 border-dashed border-slate-300 p-6 rounded text-center text-slate-500">
                                 Drag & drop redacted evidence (PDF) here
                             </div>
                             <button className="bg-blue-600 text-white py-2 rounded font-medium">Submit for Verification</button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'inbox' && <CommunicationHub />}

            {activeTab === 'settings' && <SettingsView />}

            {activeTab === 'billing' && (
                <div className="space-y-8">
                    <h2 className="text-2xl font-bold text-slate-900">Subscription & Billing</h2>
                    <div className="bg-white rounded-lg border border-slate-200 p-6 flex justify-between items-center">
                        <div>
                            <div className="text-sm text-slate-500 uppercase tracking-wide mb-1">Current Plan</div>
                            <div className="text-3xl font-bold text-slate-900 capitalize">{MOCK_SUBSCRIPTION.planId}</div>
                            <div className="text-sm text-slate-500 mt-1">Renews on {MOCK_SUBSCRIPTION.nextBillingDate}</div>
                        </div>
                        <button onClick={() => setShowCheckout(true)} className="px-4 py-2 border border-blue-600 text-blue-600 rounded font-medium hover:bg-blue-50">Change Plan</button>
                    </div>
                    
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 font-medium text-slate-900">Invoice History</div>
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Invoice</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                                {MOCK_INVOICES.map(inv => (
                                    <tr key={inv.id}>
                                        <td className="px-6 py-4 text-sm text-slate-900">{inv.date}</td>
                                        <td className="px-6 py-4 text-sm text-slate-900">${inv.amount}</td>
                                        <td className="px-6 py-4"><span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full capitalize">{inv.status}</span></td>
                                        <td className="px-6 py-4 text-right text-sm text-blue-600 hover:underline cursor-pointer">Download</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'analytics' && (
                <div className="space-y-8">
                    <h2 className="text-2xl font-bold text-slate-900">Performance Analytics</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-lg border border-slate-200">
                            <h3 className="font-bold text-slate-900 mb-6">Conversion Funnel</h3>
                            <div className="space-y-4">
                                {MOCK_CONSULTANCY_ANALYTICS.funnel.map((item, idx) => (
                                    <div key={item.name}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span>{item.name}</span>
                                            <span className="font-medium">{item.value}</span>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-blue-500" style={{width: `${(item.value / 1200) * 100}%`}}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-lg border border-slate-200">
                            <h3 className="font-bold text-slate-900 mb-6">Market Benchmarks</h3>
                            <div className="space-y-6">
                                {MOCK_CONSULTANCY_ANALYTICS.peerComparison.map(metric => (
                                    <div key={metric.metric}>
                                        <div className="flex justify-between text-sm font-medium mb-2">
                                            <span>{metric.metric}</span>
                                            <span className={metric.myValue > metric.marketAvg ? 'text-green-600' : 'text-amber-600'}>
                                                {metric.myValue > metric.marketAvg ? 'Above Avg' : 'Below Avg'}
                                            </span>
                                        </div>
                                        <div className="h-4 bg-slate-100 rounded-full relative">
                                            <div className="absolute top-0 h-full bg-blue-500 rounded-full" style={{width: '50%'}}></div> {/* Mock positioning */}
                                            <div className="absolute top-0 h-full w-1 bg-slate-900 z-10" style={{left: '50%'}}></div>
                                        </div>
                                        <div className="flex justify-between text-xs text-slate-500 mt-1">
                                            <span>You: {metric.myValue}</span>
                                            <span>Avg: {metric.marketAvg}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        
        {showCheckout && <CheckoutModal isOpen={showCheckout} onClose={() => setShowCheckout(false)} />}
    </div>
  );
};
